import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { getApiHeaders, API_TYPES } from '../utils/apiHeaders';
import { getLicenseInfo as getLicenseInfoHelper } from '../utils/billingHelpers';

const API_BASE_URL = import.meta.env.VITE_DB_API_URL || '/api';

export const useUserManagement = (subscriptionData, onSuccess) => {
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const { getAccessToken } = useAuth();
const { getUserEmail } = useAuth();

  // Get current admin's ID from subscription data
  const getCurrentAdminId = useCallback(() => {
    const currentUserEmail = getUserEmail();
    const currentAdmin = subscriptionData?.admins?.find(admin => 
      admin.email === currentUserEmail || admin.Email === currentUserEmail
    );
    return currentAdmin?.adminID || currentAdmin?.AdminID;
  }, [subscriptionData, getUserEmail]);

  // Get subscription license information using billing helper
  // Now supports both PerUser and FlatRate billing models
  const getLicenseInfo = useCallback(() => {
    return getLicenseInfoHelper(subscriptionData);
  }, [subscriptionData]);

  // Get users for the current subscription
  const getUsers = useCallback(async () => {
    if (!subscriptionData?.subscriptionID && !subscriptionData?.SubscriptionID) {
      throw new Error('No subscription ID available');
    }

    setLoading(true);
    setError(null);

    try {
      const subscriptionId = subscriptionData.subscriptionID || subscriptionData.SubscriptionID;
      const token = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/GetUsers?subscriptionId=${subscriptionId}`, {
        method: 'GET',
        headers: getApiHeaders(token, API_TYPES.DATABASE)
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to get users: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [subscriptionData, getAccessToken]);

  // Add a new user (fullName is now required)
  const addUser = useCallback(async (email, fullName) => {
    if (!subscriptionData?.subscriptionID && !subscriptionData?.SubscriptionID) {
      throw new Error('No subscription ID available');
    }

    if (!fullName || !fullName.trim()) {
      throw new Error('Display Name is required');
    }

    setLoading(true);
    setError(null);

    try {
      const subscriptionId = subscriptionData.subscriptionID || subscriptionData.SubscriptionID;
      const token = await getAccessToken();

      const requestBody = {
        subscriptionID: subscriptionId,
        email: email,
        fullName: fullName
      };

      const userResponse = await fetch(`${API_BASE_URL}/CreateUser`, {
        method: 'POST',
        headers: getApiHeaders(token, API_TYPES.DATABASE),
        body: JSON.stringify(requestBody)
      });

      if (userResponse.ok) {
        const result = await userResponse.json();
        // Callback removed - let component handle success silently
        return result;
      } else {
        const errorText = await userResponse.text();
        throw new Error(`Failed to create user: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [subscriptionData, getAccessToken, onSuccess]);

  // Delete a user
  const deleteUser = useCallback(async (userId, userEmail) => {
    const adminId = getCurrentAdminId();
    if (!adminId) {
      throw new Error('Unable to determine current admin ID');
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();

      const dbDeleteResponse = await fetch(`${API_BASE_URL}/DeleteUser?adminId=${adminId}`, {
        method: 'DELETE',
        headers: getApiHeaders(token, API_TYPES.DATABASE),
        body: JSON.stringify({
          userID: userId
        })
      });

      if (dbDeleteResponse.ok) {
        const result = await dbDeleteResponse.json();
        // Callback removed - let component handle success silently
        return result;
      } else {
        const errorText = await dbDeleteResponse.text();
        throw new Error(`Failed to delete user: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCurrentAdminId, getAccessToken, onSuccess]);

  // Update user license status
  const updateUserLicense = useCallback(async (userId, hasActiveLicense) => {
    const adminId = getCurrentAdminId();
    if (!adminId) {
      throw new Error('Unable to determine current admin ID');
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/UpdateUserLicense?adminId=${adminId}`, {
        method: 'PUT',
        headers: getApiHeaders(token, API_TYPES.DATABASE),
        body: JSON.stringify({
          userID: userId,
          hasActiveLicense: hasActiveLicense
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Callback removed - let component handle success silently
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to update license: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCurrentAdminId, getAccessToken, onSuccess]);

  // Assign reports to a user
  const assignReports = useCallback(async (userId, reportIds) => {
    const adminId = getCurrentAdminId();
    if (!adminId) {
      throw new Error('Unable to determine current admin ID');
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/AssignReportsToUser?adminId=${adminId}`, {
        method: 'POST',
        headers: getApiHeaders(token, API_TYPES.DATABASE),
        body: JSON.stringify({
          userID: userId,
          reportIDs: reportIds
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Callback removed - let component handle success silently
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to assign reports: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCurrentAdminId, getAccessToken, onSuccess]);

  // Remove report assignments from a user
  const removeReportAssignments = useCallback(async (userId, reportIds) => {
    const adminId = getCurrentAdminId();
    if (!adminId) {
      throw new Error('Unable to determine current admin ID');
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/UnassignReportsFromUser?adminId=${adminId}`, {
        method: 'DELETE',
        headers: getApiHeaders(token, API_TYPES.DATABASE),
        body: JSON.stringify({
          userID: userId,
          reportIDs: reportIds
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Callback removed - let component handle success silently
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to remove report assignments: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCurrentAdminId, getAccessToken, onSuccess]);

  // Get user report assignments
  const getUserReportAssignments = useCallback(async (userId) => {
    const adminId = getCurrentAdminId();
    if (!adminId) {
      throw new Error('Unable to determine current admin ID');
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/GetUserReportAssignments?userId=${userId}&adminId=${adminId}`, {
        method: 'GET',
        headers: getApiHeaders(token, API_TYPES.DATABASE)
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to get user report assignments: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCurrentAdminId, getAccessToken]);

  return {
    loading,
    error,
    getUsers,
    addUser,
    deleteUser,
    updateUserLicense,
    assignReports,
    removeReportAssignments,
    getUserReportAssignments,
    getLicenseInfo,
    currentAdminId: getCurrentAdminId()
  };
};